/**
 * Aceleres: descreve a mensagem citada — autor, cor e o que mostrar no lugar
 * do conteúdo.
 *
 * Duas telas fazem a mesma pergunta: o card do compositor (ReplyToMessage.vue)
 * e a citação dentro do balão (components-next/message/bubbles/Base.vue). As
 * regras são as mesmas nos dois, mas os dados chegam em grafias diferentes: a
 * store entrega snake_case e a lista de mensagens passa por `useCamelCase`.
 * Por isso cada campo é lido nas duas formas.
 */
import { MESSAGE_TYPE } from 'widget/helpers/constants';
import { isGifMessage } from 'dashboard/helper/gifMediaHelper';
import { quotedAccentColor } from 'dashboard/helper/quotedMessageAccent';
import { isLottieStickerAttachment } from 'dashboard/helper/lottieStickerPlayer';
import { formatMediaDuration } from 'dashboard/helper/mediaDuration';
import {
  isStickerMessage,
  STICKER_LABEL,
} from 'dashboard/helper/stickerMessageHelper';
import {
  groupParticipantName,
  isGroupMessage,
} from 'dashboard/components-next/message/groupParticipant.js';

export { STICKER_LABEL };

// Verde do WhatsApp, o mesmo do botão de enviar: citar a própria mensagem não
// usa a cor de contato, usa a cor "sou eu".
const OWN_MESSAGE_ACCENT = '#00a884';

// Ícones do conjunto lucide já usado no compositor. Não existe ícone de GIF no
// conjunto; o de filme é o mais próximo e o rótulo ao lado já diz "GIF".
const KIND_ICONS = {
  image: 'i-lucide-image',
  video: 'i-lucide-video',
  gif: 'i-lucide-film',
  audio: 'i-ph-microphone-fill',
  file: 'i-lucide-file-text',
  location: 'i-lucide-map-pin',
  contact: 'i-lucide-user',
  fallback: 'i-lucide-link',
  ig_reel: 'i-lucide-video',
  embed: 'i-lucide-link',
};

// Tipos desenhados por <video>: o `kind` já veio normalizado de
// describeQuotedMessage (o GIF do WhatsApp vira 'gif', não 'video').
const VIDEO_KINDS = new Set(['video', 'ig_reel']);

// Quem tem miniatura para mostrar; o resto fica só com ícone + rótulo.
const KINDS_WITH_THUMB = new Set(['image', 'gif', ...VIDEO_KINDS]);

const field = (source, camel, snake) => source?.[camel] ?? source?.[snake];

const isOutgoingMessage = message => {
  const messageType = field(message, 'messageType', 'message_type');
  return (
    messageType === MESSAGE_TYPE.OUTGOING || String(messageType) === 'outgoing'
  );
};

// Aceleres: a miniatura da citação — o quadradinho à direita, como no
// WhatsApp. Foto, GIF e vídeo mostram imagem; nos demais tipos o ícone com o
// rótulo já diz tudo.
//
// O GIF chega de duas formas (ver gifMediaHelper): um .gif de verdade, que é
// anexo de imagem e anima sozinho no <img>, e o GIF do WhatsApp, que é um MP4
// mudo. Para o MP4 o Active Storage só gera `thumb_url` quando conseguiu
// pré-visualizar o vídeo; quando não gerou, a própria miniatura é um <video>
// parado no primeiro quadro — por isso o consumidor precisa saber qual dos
// dois vai desenhar.
//
// Aceleres 2026-08-17: o vídeo entrou na lista e, na prática, é sempre esse
// segundo caminho — `config.active_storage.previewers = []` desliga a geração
// de prévia, então `thumb_url` de vídeo vem sempre vazio (Attachment#thumb_url
// depende de `file.previewable?`). O ramo do `thumb_url` fica como reserva,
// caso os previewers voltem.
const quotedMediaThumb = (attachment, kind) => {
  if (!attachment || !KINDS_WITH_THUMB.has(kind)) return null;

  const fileType = String(
    field(attachment, 'fileType', 'file_type') || ''
  ).toLowerCase();
  const thumbUrl = field(attachment, 'thumbUrl', 'thumb_url') || '';
  const dataUrl = field(attachment, 'dataUrl', 'data_url') || '';

  // O reel do Instagram e a story chegam como anexo externo, com `file_type`
  // próprio e sem blob — mas continuam sendo um vídeo para desenhar.
  if (fileType === 'video' || VIDEO_KINDS.has(kind)) {
    // Sem `thumb_url` o vídeo é desenhado direto; o `#t=0.1` faz o navegador
    // buscar o primeiro quadro em vez de exibir um retângulo preto.
    if (thumbUrl) return { url: thumbUrl, isVideo: false };
    return dataUrl ? { url: `${dataUrl}#t=0.1`, isVideo: true } : null;
  }

  // O GIF animado só se move a partir do arquivo original: `thumb_url` é um
  // quadro estático redimensionado.
  const url = kind === 'gif' ? dataUrl || thumbUrl : thumbUrl || dataUrl;
  return url ? { url, isVideo: false } : null;
};

const stickerImageUrl = attachment => {
  const fileType = String(
    field(attachment, 'fileType', 'file_type') || ''
  ).toLowerCase();

  // A figurinha vetorial (Lottie) chega como JSON e não é desenhável por
  // <img> — nesse caso vale o ícone, não a miniatura.
  if (fileType !== 'image') return '';

  return (
    field(attachment, 'thumbUrl', 'thumb_url') ||
    field(attachment, 'dataUrl', 'data_url') ||
    ''
  );
};

export function describeQuotedMessage(message, t) {
  const contentAttributes =
    field(message, 'contentAttributes', 'content_attributes') || {};
  const attachment = message?.attachments?.[0] || null;
  const isOutgoing = isOutgoingMessage(message);

  let authorName = '';
  if (isOutgoing) {
    authorName = 'Você';
  } else {
    authorName =
      (isGroupMessage(contentAttributes) &&
        groupParticipantName(contentAttributes)) ||
      message?.sender?.name ||
      'Contato';
  }

  const isSticker = isStickerMessage(message);

  // O tipo de EXIBIÇÃO não vive no anexo: o GIF do WhatsApp é um MP4 mudo com
  // `file_type: 'video'` (ver gifMediaHelper).
  let kind = '';
  if (attachment) {
    const fileType = String(
      field(attachment, 'fileType', 'file_type') || ''
    ).toLowerCase();
    kind = isGifMessage(message)
      ? 'gif'
      : (KIND_ICONS[fileType] && fileType) || 'file';
  }

  let label = '';
  if (isSticker) {
    label = STICKER_LABEL;
  } else if (kind === 'audio') {
    // O áudio citado mostra a DURAÇÃO ao lado do microfone, como no WhatsApp —
    // "Mensagem de áudio" não diz nada sobre qual áudio é. A duração vem do
    // blob (o Active Storage analisa o arquivo) e, no que veio pela WAME, do
    // que a ponte gravou.
    label = formatMediaDuration(
      Number(
        attachment?.duration ??
          field(
            contentAttributes,
            'wameMediaDurationSeconds',
            'wame_media_duration_seconds'
          )
      )
    );
  } else if (kind === 'file') {
    // Documento mostra o nome do arquivo, como no WhatsApp; sem nome, cai no
    // rótulo genérico.
    label =
      field(attachment, 'fileName', 'file_name') ||
      t(`CHAT_LIST.ATTACHMENTS.${kind}.CONTENT`);
  } else if (kind) {
    label = t(`CHAT_LIST.ATTACHMENTS.${kind}.CONTENT`);
  }

  const mediaThumb = isSticker ? null : quotedMediaThumb(attachment, kind);

  // A figurinha vetorial não tem imagem: o desenho é a animação Lottie, que a
  // miniatura da citação renderiza no mesmo player do balão.
  const isLottieSticker = isSticker && isLottieStickerAttachment(attachment);

  return {
    isOutgoing,
    authorName,
    accentColor: isOutgoing
      ? OWN_MESSAGE_ACCENT
      : quotedAccentColor(authorName),
    isSticker,
    stickerUrl:
      isSticker && attachment && !isLottieSticker
        ? stickerImageUrl(attachment)
        : '',
    stickerLottieUrl: isLottieSticker
      ? field(attachment, 'dataUrl', 'data_url') || ''
      : '',
    // `kind` sai junto do ícone porque foto e GIF são desenhados à mão (ver
    // QuotedMediaIcon.vue) — a classe lucide não descreve mais os dois.
    kind: isSticker ? 'sticker' : kind,
    icon: isSticker ? 'i-ph-smiley-sticker' : KIND_ICONS[kind] || '',
    label,
    mediaThumbUrl: mediaThumb?.url || '',
    mediaThumbIsVideo: Boolean(mediaThumb?.isVideo),
  };
}
